import { useCallback, useEffect } from 'react';
import { DragAndDropFile } from 'taboola-ultimate-ui';
import { useFileFieldValue } from 'modules/campaigns/modules/common-campaign-form';
import { ONLY_IMAGE_FILES } from 'modules/campaigns/modules/creative-creator/config/fileTypeConfigs';
import { MediaDragAndDropContent } from 'modules/campaigns/modules/creative-editor/components/MediaDragAndDrop/MediaDragAndDropContent';
import { withIndication } from 'modules/errors';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useOperationsApi } from 'services/api';
import { UploadImageTooltip } from '../UploadImageTooltip';
import { thumbnailValidations } from '../utils/thumbnailValidations';
import styles from './dragAndDrop.module.scss';

const IndicationWrapper = withIndication(({ children }) => <div className={styles['media-container']}>{children}</div>);

export const DragAndDrop = ({ setImageData }) => {
    const { uploadThumbnail } = useOperationsApi();

    const { accountId } = useAccount();
    const {
        onDrop,
        value: imageItem,
        isLoading,
        cleanValue,
        cleanCache,
        isError: isUploadError,
        failedValidationData,
    } = useFileFieldValue({
        validations: thumbnailValidations,
        field: 'imageItem',
        fileUploader: ({ file }) => uploadThumbnail(accountId, file),
    });

    const onDropHandler = useCallback(
        files => {
            cleanCache();
            onDrop(files);
        },
        [cleanCache, onDrop]
    );

    useEffect(() => {
        if (isUploadError) {
            setImageData(null);
        }
        const imageData = imageItem?.uploadedData;
        if (imageData) {
            setImageData(imageData);
            cleanValue();
            cleanCache();
        }
    }, [imageItem, setImageData, isUploadError, cleanValue, cleanCache]);

    return (
        <>
            <div className={styles['upload-header']}>
                <FormattedMessage
                    id="creative.editor.tab.aiGenerated.imageToMotion.uploadImage.header"
                    defaultMessage="Upload your image"
                />
                <UploadImageTooltip />
            </div>
            <IndicationWrapper {...failedValidationData}>
                <DragAndDropFile
                    multiple={false}
                    onDrop={onDropHandler}
                    className={styles['media']}
                    fileTypes={ONLY_IMAGE_FILES}
                    data-testid="upload-files"
                >
                    <div className={styles['drag-and-drop-container']}>
                        <MediaDragAndDropContent className={styles['link-container']} isUploading={isLoading} />
                    </div>
                </DragAndDropFile>
            </IndicationWrapper>
        </>
    );
};
